import { useEffect, useReducer, useRef, useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { emit } from '../../platform/analytics/analytics'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { usePageMeta } from '../../shell/PageMeta'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import {
  DEFAULT_FILTERS,
  PAGE_SIZE_CHOICES,
  TYPE_LABEL,
  type MerchantFilters,
} from './merchantFilters'
import { MerchantForm } from './MerchantForm'
import { MerchantSharing } from './MerchantSharing'
import { MerchantLogoField } from './MerchantLogoField'
import { MerchantsToolbar } from './MerchantsToolbar'
import {
  useDeleteMerchant,
  useMerchants,
  useOwnedMerchantsTotal,
  type Merchant,
  type MerchantPage,
} from './useMerchants'
import './merchants.css'

/**
 * The empty title, computed from the ACTIVE FILTERS rather than the data.
 *
 * "You have no merchants" is a different claim from "nothing you typed
 * matched", and `useMerchants` collapses both into the same `empty` status —
 * `isEmpty: (page) => page.total === 0` in `useMerchants.ts` cannot see which
 * one it is. This is the one place that tells them apart, and it is exported
 * so both branches are under test directly: Task 4 is what wires a search box
 * to `dispatch`, so today nothing in this screen can actually REACH the
 * second branch through the rendered UI, and a test built only around
 * `<Merchants />` could not exercise it either.
 */
export function emptyTitleFor(filters: MerchantFilters): string {
  const search = filters.search.trim()
  return search === '' ? 'No merchants yet.' : `No merchants match “${search}”.`
}

/**
 * The header's ownership split — "N system · N added by you" — the design
 * that arrived 2026-08-21 in place of the plain "N merchants" count.
 *
 * `total` is the MAIN list's own total: everything the signed-in user can
 * see, global merchants included (the API's own default). `ownedTotal` is
 * the separate, lightweight `include_global=false` total
 * (`useOwnedMerchantsTotal`) — the user/household count alone. System is the
 * difference, not a second fetch: the API has no `include_global=false`-only
 * complement endpoint, and subtracting is exact because both numbers come
 * from the same underlying set at (near enough) the same moment.
 *
 * `ownedTotal === undefined` while that second query is loading or has
 * failed — the plain total is what this falls back to rather than blocking
 * or erroring the whole header on a query backing a few words of secondary
 * text.
 *
 * **`system` cannot go negative, by construction.** `total` and `ownedTotal`
 * are two INDEPENDENT queries — see the caller below, which only ever passes
 * a real `ownedTotal` while `filters` sit at `DEFAULT_FILTERS`, the one
 * condition under which `total` is a superset of `ownedTotal`. That caller
 * discipline is the primary fix (#C1); this is the second layer, for the
 * case the two numbers still disagree — a race between the two queries
 * settling at slightly different moments, say. `ownedTotal > total` falls
 * back to the same plain form the `undefined` branch already uses, rather
 * than trust a subtraction that would print a negative "system" count.
 */
export function subtitleFor(total: number, ownedTotal: number | undefined): string {
  if (ownedTotal === undefined || ownedTotal > total) {
    return `${total} merchant${total === 1 ? '' : 's'}`
  }
  const system = total - ownedTotal
  return `${system} system · ${ownedTotal} added by you`
}

/**
 * The pager's own visible label — "range + count, always visible"
 * (2026-08-21, wave 5, D2), replacing "Page N of M". An en dash in the range,
 * not a hyphen, per Sushil's own decision.
 *
 * `to` is `from + page.merchants.length - 1`, never `page * PAGE_SIZE` — the
 * same arithmetic the `announce` callback below already uses for the
 * screen-reader region, and the same property that callback's own mutation
 * note documents: on the LAST page of a total that is not an exact multiple
 * of `PAGE_SIZE`, the two disagree, and the arithmetic version announces a
 * `to` LARGER than `total` — a number this page never actually has. Page 8 of
 * a 412-merchant list at 50/page is exactly that fixture: `from` is 351,
 * `page.merchants.length` on that page is 2 (only 412 total, not a clean
 * 400), so the real `to` is 352 while `page * PAGE_SIZE` would print 400.
 */
export function pagerLabelFor(page: MerchantPage): string {
  /* `page.pageSize`, the size the SERVER cut the rows at, not the constant or
     the size this client asked for. With "Rows per page" now adjustable the
     two can differ for one render — the request is in flight at the new size
     while the rendered page is still the old one — and computing `from` from
     the requested size would slide the range by a whole page while that lasts. */
  const from = (page.page - 1) * page.pageSize + 1
  const to = from + page.merchants.length - 1
  return `Showing ${from}–${to} of ${page.total}`
}

/** A non-empty string, trimmed — the same "word, not a blank" test the
 *  Notes column above already applies, lifted out so `canReveal` and
 *  `MerchantDetailPanel` read it once rather than repeating the trim. A type
 *  predicate, not a plain `boolean`, so `MerchantDetailPanel` can narrow
 *  `website`/`notes` from `string | null` to `string` at the same site it
 *  checks them, rather than a second non-null assertion after. */
function hasText(s: string | null | undefined): s is string {
  return s != null && s.trim() !== ''
}

/**
 * Validates and normalises `merchant.website` for use as an `href` —
 * security M1/I1, escalated.
 *
 * **Plaid returns bare domains**, not URLs: measured against the live dev
 * API with every row expanded, 24 of 24 `website` values were
 * `starbucks.com`, `uber.com`, `sephora.com` — no scheme, ever. react-router's
 * `ABSOLUTE_URL_REGEX` requires a colon to treat a `<Link to>` as external, so
 * every one was routed as an in-app path onto the app's own 404 (0 of 24
 * links worked). The single test that covered this used `https://costco.com`
 * — a shape Plaid does not return — which is why 2,156 tests never noticed.
 *
 * **Returns `null` for anything that is not `http:`/`https:`** — this is also
 * the scheme allow-list I1 asked for. A `javascript:` `website` reaching
 * `href` unvalidated is inert today only because a React 19 internal
 * (`isJavaScriptProtocol`) happens to rewrite it; this function is what
 * closes that path deliberately, rather than leaning on an internal this app
 * neither controls nor tests.
 *
 * A scheme is prepended (`https://`) rather than the value being reparsed
 * through `new URL(...).href` and returned normalised — `URL` adds a
 * trailing slash to a bare-path input (`new URL('https://costco.com').href`
 * is `'https://costco.com/'`), which would be a second, gratuitous rewrite of
 * a value the render site displays verbatim right next to the link.
 */
export function safeWebsiteHref(website: string): string | null {
  const trimmed = website.trim()
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed)
  const candidate = hasScheme ? trimmed : `https://${trimmed}`
  let url: URL
  try {
    url = new URL(candidate)
  } catch {
    return null
  }
  return url.protocol === 'http:' || url.protocol === 'https:' ? candidate : null
}

/**
 * Whether a row's chevron has anything behind it to open onto.
 *
 * `DataGrid` draws no chevron when `canExpand` is false — a row with nothing
 * to reveal must not offer an expander onto an empty box (the brief's own
 * rule). This used to be narrower — `!m.is_global || hasText(m.website) ||
 * hasText(m.notes)` — back when the expanded panel had nothing for a global
 * merchant with neither field. That is no longer true: the panel always
 * carries the merchant's own NAME as a heading and always offers "See
 * transactions" (every merchant, global or not, can have transaction
 * history), so every row has something real to reveal now, unconditionally.
 *
 * **Kept as a named function rather than inlined as `() => true`** so the
 * rule has one place to become narrower again if a real reason shows up —
 * and to record the one considered and rejected: gating on "this merchant
 * has at least one transaction" would need a per-row count `MerchantResponse`
 * does not carry (`GET /merchants` returns no transaction total), so
 * answering it here would mean an extra request per ROW rather than per
 * page — the same N+1 shape `useOwnedMerchantsTotal`'s own docblock argues
 * against paying for a single header total. It is also not obviously the
 * right rule even if it were free: "no transactions yet" is itself a fact a
 * user opening the row is entitled to see stated plainly (`MerchantTransactions`'s
 * own empty state already says so), not a reason to hide the row's own name
 * and its Edit/Delete controls behind a suppressed chevron.
 */
export function canReveal(_m: Merchant): boolean {
  return true
}

/** `useReducer`, per the brief — filter state lives here and is handed down,
 *  not read from the URL in this slice. One action for now: replace the
 *  filters wholesale. Task 4's toolbar and Task 5's pager both dispatch a
 *  full next-filters object (a debounced search reset to page 1, a page
 *  turned forward or back), so a single `'set'` action already fits both
 *  without inventing a shape neither has used yet. */
function reducer(_state: MerchantFilters, action: MerchantFilters): MerchantFilters {
  return action
}

/**
 * The delete confirmation, as its OWN component rather than inline in
 * `Merchants` — Task 10.
 *
 * `useToast()` throws outside a `<ToastProvider>` (`ToastProvider.tsx`'s own
 * guard), and `Merchants` itself has no reason to require one unconditionally
 * — the same reason `CategoryForm.tsx` calls it rather than `Categories.tsx`,
 * which `Categories.test.tsx`'s own `mount()` helper documents as staying
 * provider-free for every test that never opens the form. Only mounted when
 * `deleting !== null`, so a caller that never deletes never needs a provider
 * either.
 */
function DeleteMerchantDialog({ merchant, onClose }: { merchant: Merchant; onClose: () => void }) {
  const deleteMerchant = useDeleteMerchant()
  const toast = useToast()
  return (
    <ConfirmDialog
      open
      mode="delete_merchant"
      title={`Delete ${merchant.name}?`}
      // **The copy states what `delete_merchant`'s BODY does, not its
      // docstring.** `openapi.json` reproduces that docstring faithfully —
      // "Transactions linked to this merchant will have merchant_id set to
      // NULL (via ON DELETE SET NULL)" — and it is wrong. The handler
      // (`merchants.py:311`, `delete_merchant`) is exactly
      // `merchant.is_active = False; await db.commit()`: a soft delete. The
      // row is never removed, no foreign-key action ever fires,
      // `transactions.merchant_id` survives on every linked transaction
      // unchanged, and the merchant stays fetchable by id and reappears
      // under "Show deleted" (`includeInactive`, `MerchantsToolbar.tsx`).
      // Verified independently three times against the handler body itself
      // (final review, correctness C1) — trust the code, not the prose
      // describing it. This copy must not promise irreversibility the
      // server does not deliver.
      body={
        <>
          {merchant.name} will be removed from your merchant list — you can bring it back with
          “Show deleted”. Transactions that used it keep both their name and their link to{' '}
          {merchant.name}.
        </>
      }
      confirmLabel="Delete"
      destructive
      onCancel={onClose}
      onConfirm={async () => {
        await deleteMerchant.mutateAsync(merchant.id)
        toast.show({ kind: 'success', message: `${merchant.name} deleted` })
        onClose()
      }}
    />
  )
}

/**
 * The expanded row's own detail — what a chevron opens onto, replacing the
 * `/merchants/:id` screen the design that arrived 2026-08-21 removed.
 *
 * `DataGrid`'s own `expansion` docblock: "the detail carries its own
 * sub-grid AND a bar of actions that belong to the parent." There is no
 * sub-grid here — a merchant has no children the way a category does — so
 * this is the merchant's own name (a heading, since the row it expands from
 * may be scrolled out of view or visually separated by the time this is
 * read — an action bar with no subject is ambiguous with more than one row
 * open at once), `website`, `notes`, the logo control, and the action bar,
 * each present only when it has something real behind it.
 *
 * **"See transactions" navigates to `/merchants/:id/transactions`**, a real
 * screen — `MerchantTransactions.tsx` — rather than the module-wide
 * `/transactions?merchant_id=…` an earlier draft of this task considered:
 * that client route renders a placeholder (`M6` has not landed) that reads
 * the query string for nothing, which would have been a dead link with
 * extra steps. This one is scoped and real, and offered for every merchant
 * — global or not, since a Plaid-sourced merchant can carry transaction
 * history same as a user-created one; only Edit/Delete are gated on
 * ownership.
 *
 * Edit and Delete reuse the SAME `setForm`/`setDeleting` state `Merchants`
 * already threads to the Manage column's pencil/trash pair — one dialog
 * host per action, not two, however many rows offer a path to opening it.
 *
 * **The three actions map onto `Button`'s own variants, not one skin for
 * all three** (the defect this panel shipped with): Delete is destructive
 * (`variant="destructive"`), and Edit and "See transactions" are both
 * `variant="outline"` — same weight, same as each other. Edit is NOT
 * `variant="primary"`: `--accent`'s one lime fill per content area (hard
 * rule 9) is spent by the toolbar's own selection controls since 2026-08-21
 * (wave 5, D1 — the selected type pill and "Show deleted" switch in
 * `merchants.css`, not "New merchant" any more, which dropped to
 * `variant="outline"` in this same decision), and this panel is one MORE
 * `.merchants__detail` inside that same content area for every row a user
 * expands — a THIRD lime fill for each row open at once, on top of the two
 * rule 9 already knowingly carries. `merchants.css` names its own two
 * (the selected type pill, the "Show deleted" switch) explicitly; this is
 * the refusal for a third, not a new rule.
 */
function MerchantDetailPanel({
  merchant,
  onEdit,
  onDelete,
}: {
  merchant: Merchant
  onEdit: () => void
  onDelete: () => void
}) {
  // "See transactions" is a real navigation, not a click handler that happens
  // to call `navigate()` — see the note beside it below. Its floor now comes
  // from `ButtonLink`, which is where the paragraph that used to stand here
  // ended up: it said a hand-rolled `<Link>` wearing `Button`'s skin "has to
  // repeat that inline declaration itself or lose the floor". A fourth site
  // then failed to, and shipped 42.8px buttons on every phone width (#334).
  // The rule was right and being written down was not enough, so it is a
  // component now.
  //
  // `'inline'` below — the website link is a plain `<a>`, not `InlineLink`
  // (see the note beside it), so it has to reserve its own hit target rather
  // than getting it from that component.
  const inlineTarget = useHitTarget('inline')
  const { website, notes } = merchant
  // `null` for anything that is not `http:`/`https:`, including a bare
  // domain with no scheme at all — see `safeWebsiteHref`'s own docblock
  // (security M1/I1). A malformed or disallowed value still renders, as
  // plain text, rather than being dropped: the merchant DOES have a website
  // on file, and losing that information silently would be its own defect.
  const websiteHref = hasText(website) ? safeWebsiteHref(website) : null

  return (
    <div className="merchants__detail">
      <h3 className="merchants__detail-name">{merchant.name}</h3>
      {hasText(website) &&
        (websiteHref === null ? (
          <p className="merchants__detail-website">{website}</p>
        ) : (
          <p className="merchants__detail-website">
            {/* A plain `<a>`, not `InlineLink`/react-router's `Link` — this
                destination is external by definition (a merchant's own
                website), and `Link`'s `ABSOLUTE_URL_REGEX` is exactly what
                routed a schemeless domain in-app onto the app's own 404.
                `.merchants__detail-website-link` (`merchants.css`), not the
                shared `.inline-link` — that selector's own ownership guard
                (`controlTokens.test.ts`) requires exactly one file to use
                it, and `InlineLink.tsx` already is; see that class's own
                docblock for why `inline-flex` is load-bearing, not
                cosmetic, for the inline `min-height`/`min-width` below. */}
            <a
              href={websiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="merchants__detail-website-link hoverable"
              style={{ minHeight: inlineTarget, minWidth: inlineTarget }}
            >
              {website}
            </a>
          </p>
        ))}
      {hasText(notes) && <p className="merchants__detail-notes">{notes}</p>}
      {/* Global merchants get neither the logo field nor Edit/Delete —
          shared reference data belongs to nobody's household to edit, same
          rule the Manage column's own "System" marker follows. "See
          transactions" is unconditional, below. */}
      {!merchant.is_global && <MerchantLogoField merchant={merchant} />}
      <div className="merchants__detail-actions">
        {/* `size="panel"` — the same floor `Button`'s own docblock already
            enforces inline via `useHitTarget`, so nothing here needs to
            restate it. Order matches the design's own: Edit · See
            transactions · Delete. `variant="outline"`, not `"primary"` — see
            this function's own docblock: rule 9's one lime fill in this
            content area is spent by the toolbar's selection controls
            (2026-08-21, D1), not by "New merchant" any more. */}
        {!merchant.is_global && (
          <Button type="button" variant="outline" size="panel" onClick={onEdit}>
            Edit merchant
          </Button>
        )}
        {/* `ButtonLink`, NOT `<Button onClick={navigate}>`. This is a
            DESTINATION (`/merchants/:id/transactions`, a real route) and the
            element has to say so: a `<button>` that navigates cannot be
            middle-clicked or opened in a new tab, carries no `href` for the
            status bar, and is invisible to anything that harvests the page's
            outgoing links.

            This comment used to end "…`Button` has no `as` prop … this is
            that third site", arguing for the hand-copy that stood here. A
            fourth site then made the same copy and omitted its `min-height`,
            which is issue 334. The argument was right and the remedy was one
            site too late; `ButtonLink` is the remedy now. */}
        <ButtonLink
          to={`/merchants/${merchant.id}/transactions`}
          variant="outline"
          size="panel"
        >
          See transactions
        </ButtonLink>
        {/* Share / Stop sharing: renders only for the owner, in a household with someone else in it. */}
        <MerchantSharing merchant={merchant} />
        {!merchant.is_global && (
          <Button type="button" variant="destructive" size="panel" onClick={onDelete}>
            Delete merchant
          </Button>
        )}
      </div>
    </div>
  )
}

/** Shared by the live button and its `ButtonPreview` in the locked gate (#578). */
const NEW_MERCHANT_VARIANT = 'primary'
const NEW_MERCHANT_SIZE = 'page'
const NEW_MERCHANT_LABEL = 'New merchant'

/**
 * The merchants list.
 *
 * Search, the type filter and "Show deleted" (Task 4) live in
 * `MerchantsToolbar`. "New merchant" — gated behind `custom_merchants` since
 * Task 6 — moved out of that toolbar and into this screen's own header, then
 * (2026-08-21, wave 5, D4) INTO the same single toolbar row as those three
 * controls and the title, Sushil's own design review against a live
 * screenshot: "Title · Search · Show deleted · Type filter · New merchant",
 * one line. Task 5 adds the pager inside the success branch below. All
 * dispatch into the one reducer declared above.
 *
 * **No detail screen.** The design that arrived 2026-08-21 has no
 * `/merchants/:id` — a row's chevron expands it in place instead, the same
 * `expansion` shape `CategoryTable.tsx` already uses (see `canReveal` and
 * `MerchantDetailPanel` below).
 */
export function Merchants() {
  const [filters, dispatch] = useReducer(reducer, DEFAULT_FILTERS)
  const state = useMerchants(filters)
  // The header's ownership-split subtitle — its own lightweight query, its
  // own cache key, never the whole list paged just to count a total.
  const owned = useOwnedMerchantsTotal()
  // The ownership split is only a valid subtraction when `total` (above,
  // `useMerchants(filters)`) and `owned.total` (`OWNED_TOTAL_PARAMS`, frozen
  // and never filtered) are counting the SAME set — i.e. only at
  // `DEFAULT_FILTERS`. A search, a type filter or "Show deleted" narrows
  // `total` alone, which is what let a search matching 2 of a user's 5 rows
  // render "-3 system · 5 added by you" (#C1). `undefined` here reuses
  // `subtitleFor`'s own existing fallback — the plain `${total} merchant(s)`
  // form — rather than adding a second one.
  /* One predicate, two readers — the ownership subtitle and the empty state's
     action both mean "the user has narrowed nothing". Two copies of this
     expression would drift the day a fourth filter is added. */
  const filtersAreDefault =
    filters.search.trim() === '' && filters.type === null && !filters.includeInactive
  const ownedTotalForSubtitle = filtersAreDefault ? owned.total : undefined
  // Published to the chrome's top bar rather than rendered on the canvas —
  // Sushil moved both the title and this count up on 2026-08-21. `null` while
  // the page is still loading, so the bar shows no line at all rather than the
  // word "Loading…" sitting beside the breadcrumb.
  usePageMeta(
    state.status === 'success'
      ? subtitleFor(state.data.total, ownedTotalForSubtitle)
      : state.status === 'empty'
        ? subtitleFor(0, ownedTotalForSubtitle)
        : null,
  )
  /* Architect I3. The same read `<PremiumGate>` makes internally, lifted here
     so the event can fire from the one place this module owns — `PremiumGate`
     has no seam of its own for "I just rendered locked". Identical shape to
     `Categories.tsx`, deliberately: two gates announcing themselves two
     different ways is how the vocabulary drifts.

     Fires once per TRANSITION into locked, not once per render — a breakpoint
     change or a parent re-render would otherwise re-announce a gate whose lock
     state never moved. */
  const entitled = useEntitlement('custom_merchants')
  const announcedLocked = useRef(false)
  useEffect(() => {
    if (!entitled && !announcedLocked.current) {
      announcedLocked.current = true
      emit('locked_create_shown', { entitlement: 'custom_merchants' })
    } else if (entitled) {
      announcedLocked.current = false
    }
  }, [entitled])

  const phone = useBreakpoint() === 'phone'
  const target = useHitTarget('page')
  // Ids of the open rows — the same `Set<string>` shape `CategoryTable.tsx`
  // keeps for its own `open`, and for the identical reason: a flag on the
  // node would have to live somewhere the row data does not (a `Merchant` is
  // API-shaped, not UI state).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  /**
   * `MerchantForm`'s state, as one value rather than two flags — a `boolean`
   * for "creating" plus a `Merchant | null` for "editing" can both be truthy
   * at once, a combination `MerchantForm` has no rendering for and this
   * screen would then have to invent an order of precedence for. `'closed'`
   * unmounts the dialog entirely; `'create'` opens it with no `editing`
   * merchant (Task 7); a `Merchant` opens it pre-filled (Task 8) — global or
   * not, `MerchantForm` itself decides what to render (a global row is
   * read-only; there is no fork path — see its own docblock).
   */
  const [form, setForm] = useState<'closed' | 'create' | Merchant>('closed')
  // The merchant a delete confirmation is currently open against — Task 10.
  // Kept separate from `form`: `MerchantForm` and `ConfirmDialog` are two
  // different `Overlay` hosts, and the two can legitimately never be open
  // together in this screen's own flows (Delete has no entry point from
  // inside the form), but nothing stops a future caller from wanting both,
  // and folding a THIRD dialog kind into `form`'s union would force this
  // component to also invent an order of precedence for two dialogs wanting
  // to be open — the exact problem the docblock on `form` above already
  // names for create-vs-edit.
  const [deleting, setDeleting] = useState<Merchant | null>(null)

  const columns: Column<Merchant>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      // `minmax(0, …)`, not a bare `fr` — DataGrid's own docblock measured an
      // `fr` track refusing to shrink below its content's min-content and
      // overflowing the grid below ~360px.
      width: 'minmax(0, 1.8fr)',
      // Plain text, not a button — there is no detail SCREEN to activate any
      // more (the design that arrived 2026-08-21 has none; see `Merchants`'s
      // own docblock). The row's own activation control is now the chevron
      // `DataGrid` draws in this same cell when `expansion.canExpand` is
      // true, which is a real `<button aria-expanded>` DataGrid itself
      // renders and gives its own accessible name to — this cell does not
      // need to duplicate that.
      render: (m) => (
        <span className="merchants__cell">
          <MerchantAvatar merchant={m} size="row" />
          <span className="merchants__name">{m.name}</span>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: 'minmax(0, 0.8fr)',
      render: (m) => <span className="merchants__meta">{TYPE_LABEL[m.type]}</span>,
    },
    {
      key: 'notes',
      header: 'Notes',
      width: 'minmax(0, 1.6fr)',
      // An em-dash for "none", the same word-not-a-blank rule
      // `CategoryTable`'s Subs column follows — an empty cell reads to a
      // screen reader as a missing value, not as "there is nothing here".
      render: (m) => (
        <span className="merchants__notes" title={m.notes ?? undefined}>
          {m.notes && m.notes.trim() !== '' ? m.notes : '—'}
        </span>
      ),
    },
    {
      key: 'manage',
      header: 'Manage',
      width: '96px',
      align: 'end',
      // A global merchant gets NEITHER control — absent, not disabled, the
      // same rule `CategoryTable`'s Manage capsule follows for a system
      // category: shared reference data belongs to nobody's household to
      // rename or remove, and users must not fork one into a personal copy
      // either (product decision 2026-08-21 — see `MerchantForm.tsx`'s own
      // docblock). A muted "System" marker stands in its place rather than
      // an empty cell — every row in today's dev data is global, and an
      // entirely blank labelled column reads as broken rather than as
      // "nothing to do here" (see `.merchants__manage-empty` in
      // `merchants.css`).
      render: (m) =>
        m.is_global ? (
          <span className="merchants__manage-empty">System</span>
        ) : (
          <span className="merchants__manage-cell">
            <button
              type="button"
              className="merchants__manage-btn hoverable"
              style={{ minHeight: target, minWidth: target }}
              aria-label={`Edit ${m.name}`}
              onClick={() => setForm(m)}
            >
              <Icon glyph={Pencil} size={14} />
            </button>
            <button
              type="button"
              className="merchants__manage-btn hoverable"
              style={{ minHeight: target, minWidth: target }}
              aria-label={`Delete ${m.name}`}
              onClick={() => setDeleting(m)}
            >
              <Icon glyph={Trash2} size={14} />
            </button>
          </span>
        ),
    },
  ]

  // Merchant and Manage survive on a phone; Type and Notes are dropped rather
  // than squeezed, the same trade `CategoryTable` makes for Subs/Auto-rules/
  // Status. Notes is recoverable even here — the row's own expanded detail
  // (`canReveal`/`MerchantDetailPanel` above) renders `notes` in full
  // regardless of breakpoint, dropping only the column's own truncated
  // preview and its `title` tooltip. Type has no such recovery — it is not
  // one of the fields the detail panel shows — which is the honest cost of
  // dropping it from the table with nowhere else to put it today.
  const shown = phone ? columns.filter((c) => c.key === 'merchant' || c.key === 'manage') : columns

  return (
    <>
      {/* ONE ROW since 2026-08-21 (wave 5, D4): title · search · "Show
          deleted" · type filter · "New merchant", Sushil's own order against
          a live screenshot. `.merchants__count` (this component) and
          `.merchants-toolbar__cta` (below) are this file's two direct
          children of the row; `<MerchantsToolbar>` contributes the middle
          two groups as siblings of both (see its own docblock for why it
          renders no wrapper of its own any more). ON THE CANVAS, not inside
          `Panel` — the design that arrived 2026-08-21 draws the search pill
          and the filter controls above the table, on the page background,
          with the panel starting at the column-header row. `--chip`/`--edge`
          are proven against `--surface` (`categories.css`'s own toolbar,
          which still sits inside its table's `Panel`), not against the
          canvas's washed-and-meshed composite — putting the old skin here
          as-is measured `compositeContrast.test.tsx` failures.
          `merchants.css`'s own `.merchants-toolbar__search`/`__type` retone
          their fill to `--surface` (opaque in both themes, unlike `--chip`'s
          translucent dark value) and their border to `--ink2` (the boundary
          the walk actually checks a capsule's edge against once nothing sits
          between it and `<body>` — `--edge` on the raw page composite
          measures under 3:1 in both themes); the "Type" label and "Show
          deleted" text move from `--ink2` to `--ink` for the same reason
          `.merchants__subtitle` below already gives — bare text with no
          opaque capsule under it, sitting directly on the wash. Rendered
          unconditionally, before `<Panel>`, so it keeps working through
          every `Async` state below — loading, error, a stale/empty page —
          since filtering is a server request, not a client-side narrowing of
          rows already rendered. */}
      <div className="merchants-toolbar">
        <MerchantsToolbar filters={filters} onChange={dispatch} />
        {/* M11-R18: gated on the control itself, so the gate fires when the
            user reaches for it — not after the form is filled. M11-R20:
            browsing is never gated (every control in `MerchantsToolbar` and
            the whole list stay reachable at every entitlement); this is the
            one place a free user is stopped. `variant="primary"` — the
            PRIMARY-ACTION role of rule 9 as amended 2026-08-21: lime belongs
            to a role, not to an element, and the toolbar's lime selection
            controls are the separate SELECTION-SYSTEM role rather than a
            competing fill. Both roles may carry lime in one content area.
            Sits last in the row, after the right-aligned type filter. */}
        <span className="merchants-toolbar__cta">
          <PremiumGate
            entitlement="custom_merchants"
            preview={
              <ButtonPreview variant={NEW_MERCHANT_VARIANT} size={NEW_MERCHANT_SIZE}>
                {NEW_MERCHANT_LABEL}
              </ButtonPreview>
            }
            onUpgrade={() => {
              /* M13 billing owns the destination; nothing here yet — same as
                 `Categories.tsx`'s identical gate. */
            }}
          >
            <Button variant={NEW_MERCHANT_VARIANT} size={NEW_MERCHANT_SIZE} onClick={() => setForm('create')}>
              {NEW_MERCHANT_LABEL}
            </Button>
          </PremiumGate>
        </span>
      </div>
      <Panel>
        <Async
          surface="merchants"
          state={state}
          skeleton={
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          }
          /* Architect I11. `docs/rules.md`: empty names the missing thing AND
             offers one action. `emptyTitleFor` already distinguishes the two
             empties; only the filtered one gets an action here.

             The first-run branch deliberately gets NONE. The action that fills
             that screen is "New merchant", which is gated on
             `custom_merchants` — offering it inside `Async`'s empty slot would
             put an ungated second route to the create form on the page, which
             is precisely the hole `G3` in the mutation report exists to
             forbid. The page's own gated CTA is a few pixels away and is the
             one way in. */
          empty={{
            title: emptyTitleFor(filters),
            ...(filtersAreDefault
              ? {}
              : {
                  action: {
                    label: 'Clear search',
                    onAct: () => dispatch({ ...DEFAULT_FILTERS }),
                  },
                }),
          }}
          // Read by `Async`'s own live region on the `loading` → `success`
          // edge only (its docblock). That is still the right edge for a
          // page TURN: `useMerchants` has no `keepPreviousData`-aware way to
          // skip `loading`, but every render this screen can actually reach
          // starts loading at least once — the FIRST time a given page/filter
          // combination is requested — so a screen-reader user turning to a
          // page for the first time still hears where they landed.
          announce={(p) => {
            const from = (p.page - 1) * p.pageSize + 1
            const to = from + p.merchants.length - 1
            return `Showing ${from} to ${to} of ${p.total}`
          }}
        >
          {(page) => {
            return (
              <>
                <DataGrid
                  columns={shown}
                  groups={[
                    // One flat band, per the brief: "no grouping — pass a
                    // single Group with label: '' and all rows, the way a
                    // flat table is expressed in this codebase." DataGrid
                    // skips the band row itself for an empty label with no
                    // subtotal, so this list is exactly as tall as its data
                    // plus the grid header — see the tests below.
                    { label: '', rows: page.merchants },
                  ]}
                  rowKey={(m) => m.id}
                  label="Merchants"
                  // Unreachable in practice — `useMerchants`'s `isEmpty`
                  // already routes a zero-row page through Async's own empty
                  // branch above, before this grid ever mounts with nothing
                  // to show. Required anyway: `DataGrid`'s own `empty` prop
                  // is mandatory so the state cannot be forgotten by a caller
                  // that DOES filter client-side.
                  empty="No merchants on this page."
                  expansion={{
                    canExpand: canReveal,
                    isExpanded: (m) => expanded.has(m.id),
                    onToggle: (m) => toggle(m.id),
                    // "Expand Costco", never "Expand" — `DataGrid`'s own
                    // `expansion.toggleLabel` docblock says so explicitly.
                    toggleLabel: (m) => `${expanded.has(m.id) ? 'Collapse' : 'Expand'} ${m.name}`,
                    renderDetail: (m) => (
                      <MerchantDetailPanel
                        merchant={m}
                        onEdit={() => setForm(m)}
                        onDelete={() => setDeleting(m)}
                      />
                    ),
                  }}
                />
                <div className="merchants__pager">
                  <Button
                    variant="outline"
                    size="panel"
                    disabled={page.page <= 1}
                    onClick={() => dispatch({ ...filters, page: page.page - 1 })}
                  >
                    Prev
                  </Button>
                  <span className="merchants__pager-label">{pagerLabelFor(page)}</span>
                  {/* Rows per page. The shared `SegmentedControl` — this is
                      the same one-of-N control as the type filter, and writing
                      a second one by hand is what the primitive exists to
                      stop. Values come from `PAGE_SIZE_CHOICES`, inside the
                      API's documented `page_size` bounds of 1..500.

                      Changing the size RESETS to page 1: "page 7 of 12" at 25
                      rows is page 2 of 3 at 100, so keeping the number would
                      land the user somewhere they never asked to be, or past
                      the end entirely. */}
                  <span className="merchants__pager-size">
                    <span className="merchants__pager-size-label" aria-hidden="true">
                      Rows
                    </span>
                    <SegmentedControl
                      label="Rows per page"
                      name="merchants-page-size"
                      options={PAGE_SIZE_CHOICES.map((n) => ({
                        value: String(n),
                        label: String(n),
                      }))}
                      value={String(filters.pageSize)}
                      onChange={(next) =>
                        dispatch({ ...filters, page: 1, pageSize: Number(next) })
                      }
                    />
                  </span>
                  <Button
                    variant="outline"
                    size="panel"
                    disabled={!page.hasMore}
                    onClick={() => dispatch({ ...filters, page: page.page + 1 })}
                  >
                    Next
                  </Button>
                </div>
              </>
            )
          }}
        </Async>
      </Panel>
      {form !== 'closed' && (
        <MerchantForm
          onClose={() => setForm('closed')}
          editing={form === 'create' ? undefined : form}
        />
      )}
      {deleting !== null && (
        <DeleteMerchantDialog merchant={deleting} onClose={() => setDeleting(null)} />
      )}
    </>
  )
}
